/**
 * The notes on screen (decision 76; part 19e log, tables D′, CP, MK and Y′; `cockatrice-ui` SPEC.md §6 and its notes
 * screens): Notes · n in the writer's bar and "Show private notes in text", the Notes panel (a sheet on a narrow screen)
 * with its tabs This chapter and Notebook, each note a card with its replies and actions, the composer under them, and the
 * selection bar's Private note. Every note here is the writer's alone (Only me) until part 38b; a Word comment is a note
 * by its author, shown as plain text (decision 59). What the writer types is the workspace's at once (part 19c's table N,
 * table CP), so nothing typed goes with the panel. Notes are never exported (decision 76).
 */
import { createEffect, createMemo, createSignal, on, untrack } from 'solid-js';
import { KEY_GROUPS } from '@cockatrice/storage';
import type { KeyGroup, NoteRecord, NoteTarget } from '@cockatrice/storage';
import { el, each, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import UiIcon from '../ui/Icon.ts';
import type { IconName } from '../ui/Icon.ts';
import IconButton from '../ui/IconButton.ts';
import InlineConfirm from '../ui/InlineConfirm.ts';
import NoteCard from '../ui/NoteCard.ts';
import SearchField from '../ui/SearchField.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import Tabs from '../ui/Tabs.ts';
import type { TabItem } from '../ui/Tabs.ts';
import TextField from '../ui/TextField.ts';
import Textarea from '../ui/Textarea.ts';
import { component } from '../ui/solid.ts';
import { focusOn } from './announce.ts';
// Notes · n and the marks check are the writer's bar's too, which moves to the library with the bar (PLAN.md step 11).
import type { NotesShow, NotesTab, Shell } from './shell.ts';

/** How long a Notebook note's title or group may be, in UTF-16 units (a name, not a note). */
export const NOTE_TITLE_MAX = 200;

/** D′1: the panel's element's ID, which Notes · n controls. */
export const NOTES_PANEL = 'notes-panel';

/** A day, an hour and a minute, in milliseconds. */
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The notes the workspace has read, none before the first read. */
function listed(shell: Pick<Shell, 'workspace'>): readonly NoteRecord[] {
  return shell.workspace.notes()?.notes ?? [];
}

/** Each listing's notes by ID, made once per listing. */
const indexes = new WeakMap<readonly NoteRecord[], ReadonlyMap<string, NoteRecord>>();
function byId(notes: readonly NoteRecord[]): ReadonlyMap<string, NoteRecord> {
  let index = indexes.get(notes);
  if (index === undefined) {
    index = new Map(notes.map((note) => [note.id, note]));
    indexes.set(notes, index);
  }
  return index;
}

/**
 * Fix-next 44 (table OR): whether `note` shows as a thread of its own: a first note; or a reply whose thread no card in
 * `notes` lists it under (OR2–OR7: its first note refused, gone, a tombstone, a reply, on another chapter, or the writer's
 * Notebook note, whose card lists no replies), so its words are on a screen, as the bundle writes it (V5-9).
 */
export function ownThread(note: NoteRecord, notes: readonly NoteRecord[]): boolean {
  if (note.parentId === null) return true;
  const parent = byId(notes).get(note.parentId);
  const listsReplies = parent !== undefined && (parent.chapterId !== null || parent.origin === 'reader' || parent.origin === 'invitee');
  return !(listsReplies && parent.deletedAt === null && parent.parentId === null && parent.chapterId === note.chapterId);
}

/** D′1: the open chapter's open notes: standing first notes on it, not resolved. */
export function openCount(shell: Pick<Shell, 'workspace'>): number {
  const { workspace } = shell;
  const chapterId = workspace.chapterId();
  const notes = listed(shell);
  return notes.filter((note) => note.chapterId === chapterId && chapterId !== null && note.deletedAt === null && !note.resolved && ownThread(note, notes)).length;
}

/** D′1: the Notebook's notes: standing first notes in it. */
export function notebookCount(shell: Pick<Shell, 'workspace'>): number {
  const notes = listed(shell);
  return notes.filter((note) => note.chapterId === null && note.deletedAt === null && ownThread(note, notes)).length;
}

/** D′1, D′10: opens the panel on `tab` (the one shown last when not given), leaving focus mode; the tab takes the focus. */
export function openNotes(shell: Shell, tab?: NotesTab): void {
  const { workspace, ui } = shell;
  if (workspace.settings().focusMode) void workspace.updateSettings({ focusMode: false });
  if (tab !== undefined) ui.setNotesTab(tab);
  ui.setNotesOpen(true);
  queueMicrotask(() => {
    focusOn(shell, '.notes-tabs [aria-selected="true"]');
  });
}

/** D′1, D′10: closes the panel, the notes' typing stored now (part 19c, N3); the focus goes to Notes, or the text. */
export function closeNotes(shell: Shell): void {
  shell.ui.setNotesOpen(false);
  void shell.workspace.flushNotes();
  queueMicrotask(() => {
    const button = document.querySelector('.open-notes');
    if (button instanceof HTMLElement && button.offsetParent !== null) button.focus();
    else shell.focusEditor();
  });
}

/**
 * D′11: Go to passage. On a narrow screen the sheet closes and the text shows first (deep-fix r3: behind the modal sheet
 * the text is inert, and on the pages' pane unseen), so the passage is selected where the writer sees it.
 */
export function goToPassage(shell: Shell, noteId: string): void {
  if (shell.mode() === 'narrow') {
    shell.ui.setNotesOpen(false);
    shell.ui.setNarrowPane('text');
    void shell.workspace.flushNotes();
  }
  shell.workspace.goToNote(noteId);
}

/**
 * NO1, CP4, Y′: the selection bar's Private note and its key: the passage chosen in the text kept for the next note of
 * This chapter, the panel opened there, the composer taking the focus; nothing with no chapter open or nothing chosen.
 */
export function noteOnChosen(shell: Shell): void {
  const { workspace, ui } = shell;
  const chapterId = workspace.chapterId();
  const anchor = workspace.chosenPassage();
  if (chapterId === null || anchor === null || !workspace.writable()) return;
  ui.setPassage({ chapterId, anchor });
  openNotes(shell, 'chapter');
  queueMicrotask(() => {
    focusOn(shell, '#note-composer');
  });
}

/** D′1: Notes · n in the bar, which opens and closes the panel. */
export function NotesButton(shell: Shell): Child {
  const { t, ui } = shell;
  return component(Button, {
    variant: 'ghost',
    size: 'sm',
    iconStart: 'notes',
    class: 'open-notes',
    get 'aria-pressed'() {
      return ui.notesOpen() ? 'true' : 'false';
    },
    get 'aria-controls'() {
      return ui.notesOpen() ? NOTES_PANEL : undefined;
    },
    onClick: () => {
      if (ui.notesOpen()) closeNotes(shell);
      else openNotes(shell);
    },
    children: [t('notes.button'), el('span', { class: 'sh-count' }, () => t('notes.buttonCount', { count: openCount(shell) }))],
  });
}

/** MK1, MK2: "Show private notes in text", a labelled check (the library's Checkbox), the device's setting. */
export function NoteMarksToggle(shell: Shell): Child {
  const { t, workspace } = shell;
  return component(Checkbox, {
    class: 'note-marks',
    label: t('notes.showInText'),
    get checked() {
      return workspace.settings().noteMarks;
    },
    onChange: (noteMarks: boolean) => void workspace.updateSettings({ noteMarks }),
  });
}

/** Y′: the selection bar while text is chosen: Private note (and its key, Ctrl+Shift+M by default). */
export function SelectionBar(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  return when(
    () => ui.textChosen() && workspace.chapterId() !== null && workspace.writable() && !workspace.settings().focusMode,
    () =>
      el(
        'div',
        { class: 'sh-selbar', role: 'toolbar', 'aria-label': t('notes.selection') },
        component(Button, {
          variant: 'ghost',
          size: 'sm',
          iconStart: 'lock',
          class: 'note-on-chosen',
          // The text keeps its selection while the button is pressed.
          onMouseDown: (event: MouseEvent) => {
            event.preventDefault();
          },
          onClick: () => {
            noteOnChosen(shell);
          },
          children: t('notes.privateNote'),
        }),
      ),
  );
}

/** A note's time as the card shows it: minutes, hours or days since, or its date past a month; and in words. */
export function noteTime(shell: Pick<Shell, 't' | 'locale'>, at: number, now: number): { readonly short: string; readonly long: string } {
  const { t } = shell;
  const since = Math.max(0, now - at);
  if (since < MINUTE) return { short: t('notes.now'), long: t('notes.now') };
  if (since < HOUR) {
    const count = Math.floor(since / MINUTE);
    return { short: t('notes.minutes', { count }), long: t('notes.minutesLong', { count }) };
  }
  if (since < DAY) {
    const count = Math.floor(since / HOUR);
    return { short: t('notes.hours', { count }), long: t('notes.hoursLong', { count }) };
  }
  if (since < 30 * DAY) {
    const count = Math.floor(since / DAY);
    return { short: t('notes.days', { count }), long: t('notes.daysLong', { count }) };
  }
  const date = new Intl.DateTimeFormat(shell.locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(at);
  return { short: date, long: date };
}

/** A note's time in the card: a `time` element, its words for assistive technology (part 38c's read view uses it too). */
export function When(shell: Shell, at: () => number): Child {
  const shown = (): { readonly short: string; readonly long: string } => noteTime(shell, at(), shell.now());
  return el('time', { class: 'sh-when', dateTime: () => new Date(at()).toISOString(), 'aria-label': () => shown().long, title: () => shown().long }, () => shown().short);
}

/** Splits text into what a reader takes as one character each (an accent and its letter, an emoji and its modifiers). */
const GRAPHEMES = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

/** The initials a Word author's avatar shows: the first character of their first two words, or "W". */
export function initials(author: string): string {
  const letters = author
    .trim()
    .split(/\s+/u)
    .flatMap((word) => [...GRAPHEMES.segment(word)].slice(0, 1).map((part) => part.segment))
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return letters === '' ? 'W' : letters;
}

/**
 * Part 19c's N1, N13: one field the writer types in, labelled (in sight, or for assistive technology alone): its text the
 * workspace's, each keystroke handed to it with what the field showed before; read-only while the workspace is paused.
 * With no typing of its own pending, it follows the stored text.
 */
function NoteField(shell: Shell, target: NoteTarget, id: string, label: string, options: { readonly long: boolean; readonly hidden?: boolean; readonly list?: string }): Child {
  const { workspace } = shell;
  // Read untracked: a field is made inside a `when`, which would make it again (and lose the focus) on each change read here.
  let shown = untrack(() => workspace.noteText(target));
  const follow = (field: HTMLInputElement | HTMLTextAreaElement): void => {
    createEffect(
      on(
        () => workspace.noteText(target),
        (text) => {
          if (workspace.typingNote(target) || text === shown) return;
          shown = text;
          // Only a changed text is put in: the same one put again would move the caret in some engines.
          if (field.value !== text) field.value = text;
        },
        { defer: true },
      ),
    );
  };
  const type = (value: string): void => {
    workspace.typeNote(target, value, shown);
  };
  const lang = (): string => workspace.book()?.metadata?.language ?? 'en-US';
  const readOnly = (): boolean => !workspace.writable();
  // A label for assistive technology alone is a hidden label, as before; one in sight is the field's own.
  const visible = options.hidden === true ? undefined : label;
  const field = options.long
    ? component(Textarea, {
        id,
        class: 'note-field',
        label: visible,
        rows: target.field === 'synopsis' ? 3 : 4,
        defaultValue: shown,
        get lang() {
          return lang();
        },
        spellcheck: true,
        get readOnly() {
          return readOnly();
        },
        ref: follow,
        onChange: type,
      })
    : component(TextField, {
        id,
        class: 'note-field',
        label: visible,
        maxLength: NOTE_TITLE_MAX,
        autocomplete: 'off',
        list: options.list,
        defaultValue: shown,
        get lang() {
          return lang();
        },
        spellcheck: true,
        get readOnly() {
          return readOnly();
        },
        ref: follow,
        onChange: type,
      });
  return visible === undefined ? [el('label', { for: id, class: 'visually-hidden' }, label), field] : field;
}

/** D′4: what a note's text shows: plain text, its line ends kept (decision 59). */
function Body(text: () => string): Child {
  return el('p', { class: 'sh-note__body' }, text);
}

/** NO8, Y′: Delete, which asks in place (InlineConfirm); Cancel takes the focus as it asks, and back to Delete once answered. */
function DeleteAsk(shell: Shell, note: () => NoteRecord, name: () => string, after: () => void): Child {
  const { t, workspace } = shell;
  return component(InlineConfirm, {
    label: t('notes.deleteShort'),
    get triggerLabel() {
      return t('notes.delete', { name: name() });
    },
    get message() {
      return t('notes.deleteAsk', { name: name() });
    },
    confirmLabel: t('notes.confirmDelete'),
    cancelLabel: t('notes.cancel'),
    // A link like the note's other actions (Edit beside it), under its card as in its reply's row.
    triggerClass: 'delete-note ui-button ui-button--link',
    get disabled() {
      return !workspace.writable();
    },
    get confirmDisabled() {
      return !workspace.writable();
    },
    onConfirm: () => void workspace.deleteNote(note().id).then(after),
  });
}

/** A note's action under its card: a link button, named for its note, off while the books are read-only. */
function Action(shell: Shell, hook: string, words: string, label: () => string, run: () => void): Child {
  return component(Button, {
    variant: 'link',
    class: hook,
    get disabled() {
      return !shell.workspace.writable();
    },
    get 'aria-label'() {
      return label();
    },
    onClick: run,
    children: words,
  });
}

/** Done under a note's fields: closes them (the typing stored), Edit taking the focus back. */
function DoneButton(run: () => void, words: string): Child {
  return el('div', { class: 'sh-note__actions' }, component(Button, { size: 'sm', class: 'done-note', onClick: run, children: words }));
}

/** A small icon before words (decorative: the words say it). */
const glyph = (name: IconName): Child => component(UiIcon, { name, size: 'sm' });

/** Part 38d: the reading link a reader's note came through, by its name when this device holds its record. */
function readerVia(shell: Shell, note: NoteRecord): string {
  const record = shell.sharing.recordOf(note);
  return record === undefined ? shell.t('notes.reader') : shell.t('notes.readerVia', { name: record.name });
}

/** A note's name in an action's words: its quote's start, its title, its text's, or what it is. */
export function noteName(shell: Pick<Shell, 't'>, note: NoteRecord): string {
  const words = (note.anchor?.quote ?? note.title).trim() || note.text.trim();
  if (words === '') return note.chapterId === null ? shell.t('notes.untitled') : shell.t('notes.wholeChapter');
  return words.length > 40 ? `${words.slice(0, 40)}…` : words;
}

/** The workspace's record of note `id`, as the listing holds it now (undefined when none). */
/** Part 38e (design round d1): an address as words for its initials: its part before the @, split at dots, dashes and underscores ("k.nilsen@…" gives K N). */
export function addressWords(address: string): string {
  const at = address.indexOf('@');
  return (at < 0 ? address : address.slice(0, at)).replace(/[._-]+/gu, ' ');
}

/** Part 38e: an invitee's name as the record holds their invite (PN13: none, "Someone invited"). */
function inviteeName(shell: Shell, note: NoteRecord): string {
  return note.author || shell.t('notes.invitee');
}

/** Part 38e: an invitee's note's role, " · Editor", when this device holds their invite. */
function inviteeRole(shell: Shell, note: NoteRecord): string {
  const role = shell.sharing.inviteOf(note.shared?.share, note.shared?.author)?.role;
  return role === undefined ? '' : ` · ${shell.t(`invite.roleName.${role}`)}`;
}

/** Part 38e (decision 76): who may see `note`, in words: Only you, a group, or the writer and one invitee. */
function seenBy(shell: Shell, note: NoteRecord): string {
  const { visibility, shared } = note;
  if (visibility === 'only-me') return shell.t('notes.onlyYou');
  if (visibility !== 'person') return shell.t(`notes.group.${visibility}`);
  const email = shell.sharing.inviteOf(shared?.share, shared?.thread)?.email;
  return shell.t('notes.group.person', { name: email ?? shell.t('notes.invitee') });
}

/** Part 38e: a note's visibility, its icon (a lock, or people) and its words (1.4.1: never the colour alone). */
function Visibility(shell: Shell, note: () => NoteRecord, id?: string): Child {
  return el(
    'span',
    { class: 'sh-vis', ...(id === undefined ? {} : { id }) },
    when(
      () => note().visibility === 'only-me',
      () => glyph('lock'),
      () => glyph('people'),
    ),
    () => seenBy(shell, note()),
  );
}

function noteOf(shell: Shell, id: string): NoteRecord | undefined {
  return listed(shell).find((note) => note.id === id);
}

/** A thread found in the text, or (part 38d) a reader's in the Notebook: never detached. */
const placedThread = (): boolean => false;

/**
 * D′4, NO5–NO8: one thread of This chapter, by its first note's ID: whose it is and when, its quote (Go to passage, D′11),
 * its text (Edit for the writer's own), its replies, then Reply, Resolve or Reopen, Edit and Delete.
 */
function ThreadCard(shell: Shell, id: string, detached: () => boolean): Child {
  const { t, workspace } = shell;
  const note = createMemo(() => noteOf(shell, id));
  const [editing, setEditing] = createSignal(false);
  const [replying, setReplying] = createSignal(false);
  // CK6 (deep-fix r2): a reply composer holding words (kept as a page went, or typed before the panel closed) is open.
  const replyOpen = (): boolean => replying() || workspace.composerText(`reply:${id}`) !== '';
  const replies = createMemo(() => listed(shell).filter((each, _, notes) => each.parentId === id && each.deletedAt === null && !ownThread(each, notes)).map((each) => each.id), [], {
    equals: (a, b) => a.length === b.length && a.every((each, index) => each === b[index]),
  });
  const base = `note-${id}`;
  return whenSome(note, (current) => {
    const word = (): boolean => current().origin === 'word';
    // Part 38d: a reading link's reader, whose words are theirs (no Edit), as a Word comment's are.
    const reader = (): boolean => current().origin === 'reader';
    // Part 38e: an invitee's note is theirs (no Edit), as a reader's is.
    const invitee = (): boolean => current().origin === 'invitee';
    // Fix-next 44: a reply shown as a thread of its own (table OR) takes no reply and can't be resolved (the store keeps
    // both for first notes, NO5, NO6); the writer's sent to a reader has no Edit, as under its thread.
    const alone = (): boolean => current().parentId !== null;
    const name = (): string => noteName(shell, current());
    // Decision 76: Word's, a reader's and an invitee's notes are the plum editor's card; the writer's Only me the dashed one.
    const variant = (): 'shared' | 'private' | 'editor' => (word() || reader() || invitee() ? 'editor' : current().visibility === 'only-me' ? 'private' : 'shared');
    const meta = when(
      word,
      () => [el('span', { class: 'sh-avatar sh-avatar--editor', 'aria-hidden': 'true' }, () => initials(current().author)), el('span', { id: `${base}-who` }, el('strong', {}, () => current().author || t('notes.wordAuthor')), ` · ${t('notes.word')}`)],
      () =>
        when(
          reader,
          () => [el('span', { class: 'sh-avatar sh-avatar--reader', 'aria-hidden': 'true' }, () => initials(current().author || t('notes.readerAuthor'))), el('span', { id: `${base}-who` }, el('strong', {}, () => current().author || t('notes.readerAuthor')), () => ` · ${readerVia(shell, current())}`)],
          () =>
            when(
              invitee,
              () => [
                el('span', { class: 'sh-avatar sh-avatar--editor', 'aria-hidden': 'true' }, () => initials(addressWords(inviteeName(shell, current())))),
                el('span', { id: `${base}-who` }, el('strong', {}, () => inviteeName(shell, current())), () => inviteeRole(shell, current())),
                Visibility(shell, current),
              ],
              // Part 38e (design round d1): the writer's shared note names them, as the design's does; Only me says so alone.
              () =>
                when(
                  () => current().visibility === 'only-me',
                  () => Visibility(shell, current, `${base}-who`),
                  () => [el('span', { class: 'sh-avatar', 'aria-hidden': 'true' }, t('notes.youInitial')), el('span', { id: `${base}-who` }, el('strong', {}, t('notes.you'))), Visibility(shell, current)],
                ),
            ),
        ),
    );
    return el(
      'li',
      { 'data-note': id },
      component(NoteCard, {
        get variant() {
          return variant();
        },
        get resolved() {
          return current().resolved;
        },
        class: 'notes-card',
        'aria-labelledby': `${base}-who`,
        meta,
        time: When(shell, () => current().writtenAt ?? current().storedAt),
        // D′11: the quote goes to its passage while the thread is found in the open chapter's text.
        get quote() {
          return current().anchor?.quote;
        },
        get onSelect() {
          return !detached() && !alone() && workspace.chapterId() === current().chapterId
            ? () => {
                goToPassage(shell, id);
              }
            : undefined;
        },
        get selectLabel() {
          return t('notes.goTo', { quote: name() });
        },
        children: [
          // Part 38e (design round d3): an invitee's Notebook note keeps its title, as the design's Notebook cards lead with theirs.
          when(
            () => current().chapterId === null && current().title.trim() !== '',
            () => el('strong', { class: 'sh-nb-title' }, () => current().title),
          ),
          when(
            editing,
            () => [
              NoteField(shell, { kind: 'note', id, field: 'text' }, `${base}-text`, untrack(() => t('notes.textLabel', { name: name() })), { long: true, hidden: true }),
              DoneButton(() => {
                setEditing(false);
                void workspace.flushNotes();
                focusOn(shell, `[data-note="${id}"] .edit-note`);
              }, t('notes.done')),
            ],
            () => Body(() => current().text),
          ),
          el(
            'ol',
            { class: 'sh-replies' },
            each(replies, (reply) => ReplyItem(shell, reply, id)),
          ),
          when(replyOpen, () =>
            ReplyComposer(shell, id, () => {
              setReplying(false);
              focusOn(shell, `[data-note="${id}"] .reply-note`);
            }),
          ),
        ],
        actions: [
          when(
            () => !replyOpen() && !alone(),
            () =>
              Action(shell, 'reply-note', t('notes.replyShort'), () => t('notes.reply', { name: name() }), () => {
                setReplying(true);
                focusOn(shell, `#reply-${id}`);
              }),
          ),
          when(
            () => !alone(),
            () =>
              component(Button, {
                variant: 'link',
                class: 'resolve-note',
                get disabled() {
                  return !workspace.writable();
                },
                get 'aria-label'() {
                  return current().resolved ? t('notes.reopen', { name: name() }) : t('notes.resolve', { name: name() });
                },
                onClick: () => void workspace.resolveNote(id, !current().resolved),
                get children() {
                  return current().resolved ? t('notes.reopenShort') : t('notes.resolveShort');
                },
              }),
          ),
          when(
            () => !word() && !reader() && !invitee() && !(alone() && current().link !== undefined) && !editing(),
            () =>
              Action(shell, 'edit-note', t('notes.editShort'), () => t('notes.edit', { name: name() }), () => {
                setEditing(true);
                focusOn(shell, `#${base}-text`);
              }),
          ),
          DeleteAsk(shell, current, name, () => {
            focusOn(shell, '#note-composer');
          }),
        ],
      }),
    );
  });
}

/** NO5: a reply under its thread: who and when, its text. */
function ReplyItem(shell: Shell, id: string, thread: string): Child {
  const { t, workspace } = shell;
  const note = createMemo(() => noteOf(shell, id));
  const [editing, setEditing] = createSignal(false);
  const base = `note-${id}`;
  return whenSome(note, (current) => {
    const name = (): string => noteName(shell, current());
    // Part 38d: a reader's reply is theirs; the writer's sent to the reader can't be taken back, so neither has Edit.
    const reader = (): boolean => current().origin === 'reader';
    const invitee = (): boolean => current().origin === 'invitee';
    const fixed = (): boolean => reader() || invitee() || current().link !== undefined;
    const who = (): string => (reader() ? current().author || t('notes.readerAuthor') : invitee() ? inviteeName(shell, current()) : t('notes.you'));
    return el(
      'li',
      { class: 'sh-reply', 'data-note': id },
      el('strong', {}, who),
      () => (invitee() ? inviteeRole(shell, current()) : ''),
      () => (current().origin === 'writer' && current().link !== undefined ? ` · ${t('notes.sent')}` : ''),
      ' ',
      When(shell, () => current().storedAt),
      // NO7, NO8 (deep-fix r4): the writer's own reply has Edit and Delete, as their notes do.
      when(
        editing,
        () => [
          NoteField(shell, { kind: 'note', id, field: 'text' }, `${base}-text`, untrack(() => t('notes.textLabel', { name: name() })), { long: true, hidden: true }),
          DoneButton(() => {
            setEditing(false);
            void workspace.flushNotes();
            focusOn(shell, `[data-note="${id}"] .edit-note`);
          }, t('notes.done')),
        ],
        () => Body(() => current().text),
      ),
      el(
        'div',
        { class: 'sh-note__actions' },
        when(
          () => !editing() && !fixed(),
          () =>
            Action(shell, 'edit-note', t('notes.editShort'), () => t('notes.edit', { name: name() }), () => {
              setEditing(true);
              focusOn(shell, `#${base}-text`);
            }),
        ),
        DeleteAsk(shell, current, name, () => {
          focusOn(shell, `[data-note="${thread}"] .reply-note, #reply-${thread}`);
        }),
      ),
    );
  });
}

/**
 * NO5, CP1: a reply's composer under its thread; Save reply stores it, Cancel keeps nothing typed in it. Part 38d, SL11:
 * in a reader's thread whose link this device holds and hasn't ended, Send reply sends it to the reader first (it is
 * stored as sent); a send that fails keeps the words and says so.
 */
function ReplyComposer(shell: Shell, parentId: string, done: () => void): Child {
  const { t, workspace, sharing } = shell;
  const key = `reply:${parentId}`;
  const parent = (): NoteRecord | undefined => noteOf(shell, parentId);
  // Deep-fix r2, RT4: the record is the power (principle 9): a reply reaches the reader without a session.
  const toReader = (): boolean => parent()?.origin === 'reader' && sharing.configured();
  const live = (): boolean => {
    const note = parent();
    return note !== undefined && sharing.recordOf(note)?.ended === null;
  };
  const [error, setError] = createSignal('');
  const [sending, setSending] = createSignal(false);
  const store = async (text: string): Promise<boolean> => {
    const note = parent();
    if (note !== undefined && toReader() && live()) {
      const sent = await sharing.reply(note, text);
      if (sent.kind === 'sent') return true;
      // Deep-fix r5, RE: meant for the reader and not sent: said, never kept here without a word (Save reply keeps it).
      setError(sent.kind === 'too-long' ? t('notes.replyTooLong') : sent.kind === 'local' || (sent.kind === 'refused' && sent.error === 'link-ended') ? t('notes.replyNotSent') : t('notes.replyFailed'));
      return false;
    }
    return (await workspace.createNote({ chapterId: null, anchor: null, title: '', group: '', text, parentId })) !== null;
  };
  return el(
    'div',
    { class: 'sh-reply-composer' },
    el('label', { for: `reply-${parentId}`, class: 'visually-hidden' }, t('notes.replyLabel')),
    component(Textarea, {
      id: `reply-${parentId}`,
      class: 'reply-field',
      rows: 2,
      placeholder: t('notes.replyPlaceholder'),
      // Part 36a, RO (deep-fix r2): read-only while the books are, so no words are typed that can't be kept.
      get readOnly() {
        return !workspace.writable();
      },
      defaultValue: untrack(() => workspace.composerText(key)),
      get lang() {
        return workspace.book()?.metadata?.language ?? 'en-US';
      },
      // CP5: the words left once a reply is stored put in; what was typed is already there.
      ref: (field: HTMLTextAreaElement) => {
        createEffect(
          on(
            () => workspace.composerText(key),
            (text) => {
              if (field.value !== text) field.value = text;
            },
            { defer: true },
          ),
        );
      },
      onChange: (value: string) => {
        workspace.typeComposer(key, value);
      },
    }),
    el(
      'div',
      { class: 'sh-note__actions' },
      component(Button, {
        size: 'sm',
        class: 'cancel-reply',
        onClick: () => {
          workspace.typeComposer(key, '');
          done();
        },
        children: t('notes.cancel'),
      }),
      component(Button, {
        variant: 'primary',
        size: 'sm',
        class: 'save-reply',
        get disabled() {
          return !workspace.writable() || workspace.composerText(key).trim() === '';
        },
        // Deep-fix r5, RS′: one send at a time.
        get 'aria-busy'() {
          return sending() ? 'true' : 'false';
        },
        onClick: () =>
            void (async () => {
              if (sending()) return;
              setError('');
              const text = workspace.composerText(key);
              setSending(true);
              const stored = await store(text).finally(() => setSending(false));
              if (!stored) return;
              const left = leftAfterSaving(workspace.composerText(key), text);
              workspace.typeComposer(key, left);
              if (left === '') done();
            })(),
        get children() {
          return toReader() && live() ? t('notes.sendReply') : t('notes.saveReply');
        },
      }),
    ),
    when(toReader, () => el('p', { class: 'sh-composer__help reply-reach' }, () => (live() ? t('notes.replyToReader') : t('notes.replyEnded')))),
    el('p', { class: 'ac-error reply-error', role: 'status' }, error),
  );
}

/** D′2, AR1–AR4: the open chapter's threads in text order: whole-chapter notes first, then the found ones by place; then the detached. */
function chapterThreads(shell: Shell, show: NotesShow): { readonly placed: readonly string[]; readonly detached: readonly string[]; readonly resolved: number } {
  const { workspace } = shell;
  const chapterId = workspace.chapterId();
  const found = workspace.found();
  const notes = listed(shell);
  const firsts = notes.filter((note) => note.chapterId === chapterId && chapterId !== null && note.deletedAt === null && ownThread(note, notes));
  // Deep-fix r8: a thread whose reply composer holds words shows whatever the filter, so the words are never out of sight.
  const shows = (note: NoteRecord): boolean => show === 'all' || (show === 'resolved' ? note.resolved : !note.resolved) || workspace.composerText(`reply:${note.id}`) !== '';
  const whole: NoteRecord[] = [];
  const placed: { readonly note: NoteRecord; readonly at: number }[] = [];
  const detached: NoteRecord[] = [];
  for (const note of firsts) {
    if (!shows(note)) continue;
    if (note.anchor === null) {
      whole.push(note);
      continue;
    }
    const range = found?.chapterId === chapterId ? found.found.get(note.id) : undefined;
    // Not found yet (the chapter just opened): where it was stored, until the find lands.
    if (range === undefined) placed.push({ note, at: note.anchor.range?.from ?? Number.MAX_SAFE_INTEGER });
    else if (range === null) detached.push(note);
    else placed.push({ note, at: range.from });
  }
  return {
    placed: [...whole.map((note) => note.id), ...placed.sort((a, b) => a.at - b.at).map(({ note }) => note.id)],
    detached: detached.map((note) => note.id),
    resolved: firsts.filter((note) => note.resolved).length,
  };
}

/** Two lists of IDs hold the same IDs in the same order. */
const sameIds = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((id, index) => id === b[index]);

/** D′2: what This chapter can show. */
const SHOWS: readonly NotesShow[] = ['open', 'resolved', 'all'];

/** D′2, D′5–D′9: This chapter: its synopsis, Show, the threads, the detached, and the counts below. */
function ChapterTab(shell: Shell, chapterId: string): Child {
  const { t, workspace, ui } = shell;
  const threads = createMemo(() => chapterThreads(shell, ui.notesShow()));
  const placed = createMemo(() => threads().placed, [], { equals: sameIds });
  const detached = createMemo(() => threads().detached, [], { equals: sameIds });
  return [
    // D′9: the chapter's synopsis, which cockatrice-ui's notes screen has no place for, behind a disclosure.
    el(
      'details',
      { class: 'sh-synopsis', open: untrack(() => workspace.noteText({ kind: 'chapter', id: chapterId, field: 'synopsis' })) !== '' },
      el('summary', {}, t('notes.synopsis')),
      NoteField(shell, { kind: 'chapter', id: chapterId, field: 'synopsis' }, 'notes-synopsis', t('notes.synopsis'), { long: true, hidden: true }),
    ),
    el(
      'div',
      { class: 'sh-filter' },
      component(SegmentedControl, {
        label: t('notes.show'),
        options: [
          { value: 'open', label: t('notes.showOpen') },
          { value: 'resolved', label: t('notes.showResolved') },
          { value: 'all', label: t('notes.showAll') },
        ],
        get value() {
          return ui.notesShow();
        },
        onChange: (value: string) => {
          for (const show of SHOWS) if (show === value) ui.setNotesShow(show);
        },
        get fullWidth() {
          return shell.mode() === 'narrow';
        },
      }),
    ),
    when(
      () => placed().length === 0 && detached().length === 0,
      () => el('p', { class: 'sh-empty-line' }, () => t(ui.notesShow() === 'resolved' ? 'notes.noneResolved' : ui.notesShow() === 'all' ? 'notes.noneAll' : 'notes.noneOpen')),
    ),
    el('ul', { class: 'sh-note-list', 'aria-label': t('notes.inText') }, each(placed, (id) => ThreadCard(shell, id, placedThread))),
    when(
      () => detached().length > 0,
      () => [
        el('h3', { class: 'sh-detached-heading', id: 'notes-detached' }, t('notes.detached')),
        el('p', { class: 'sh-help' }, t('notes.detachedHelp')),
        el('ul', { class: 'sh-note-list', 'aria-labelledby': 'notes-detached' }, each(detached, (id) => ThreadCard(shell, id, () => true))),
      ],
    ),
    when(
      () => ui.notesShow() === 'open' && threads().resolved > 0,
      () => el('p', { class: 'sh-foot-note' }, () => t('notes.resolvedCount', { count: threads().resolved })),
    ),
  ];
}

/** D′3: a Notebook note's card: its title, Only you, its text, when last edited; Edit (title, group, text) and Delete. */
function NotebookCard(shell: Shell, id: string, hold: (group: string | null) => void): Child {
  const { t, workspace } = shell;
  const note = createMemo(() => noteOf(shell, id));
  const [editing, setEditing] = createSignal(false);
  // Deep-fix r4: while editing, the card keeps its group and place (`hold`), so a pause storing its group doesn't move it.
  // Done: the fields close at once; the card goes to its group once the typing is stored, and Edit takes the focus there.
  const done = (): void => {
    setEditing(false);
    void workspace.flushNotes().then(() => {
      hold(null);
      focusOn(shell, `[data-note="${id}"] .edit-note`);
    });
  };
  const base = `note-${id}`;
  return whenSome(note, (current) => {
    const name = (): string => noteName(shell, current());
    const edit = (): void => {
      hold(current().group.trim());
      setEditing(true);
    };
    return el(
      'li',
      { 'data-note': id },
      component(NoteCard, {
        get variant() {
          return current().visibility === 'only-me' ? 'private' : 'shared';
        },
        class: 'notes-card',
        'aria-labelledby': `${base}-title`,
        meta: [el('strong', { class: 'sh-nb-title', id: `${base}-title` }, () => current().title.trim() || t('notes.untitled')), Visibility(shell, current)],
        children: [
          when(
            editing,
            () => [
              NoteField(shell, { kind: 'note', id, field: 'title' }, `${base}-edit-title`, t('notes.titleLabel'), { long: false }),
              NoteField(shell, { kind: 'note', id, field: 'group' }, `${base}-group`, t('notes.groupLabel'), { long: false, list: 'note-groups' }),
              NoteField(shell, { kind: 'note', id, field: 'text' }, `${base}-text`, t('notes.textLabelPlain'), { long: true }),
              DoneButton(done, t('notes.done')),
            ],
            () => Body(() => current().text),
          ),
          // The design's meta line under the text.
          when(
            () => !editing(),
            () => el('p', { class: 'sh-when' }, () => t('notes.edited', { when: noteTime(shell, current().editedAt, shell.now()).long })),
          ),
        ],
        actions: [
          when(
            () => !editing(),
            () =>
              Action(shell, 'edit-note', t('notes.editShort'), () => t('notes.edit', { name: name() }), () => {
                edit();
                focusOn(shell, `#${base}-edit-title`);
              }),
          ),
          DeleteAsk(shell, current, name, () => {
            hold(null);
            focusOn(shell, '.new-note');
          }),
        ],
      }),
    );
  });
}

/**
 * D′3: the Notebook's notes matching the search (title, group or text, any case), by group in the order each group was
 * first made (its first note stored), those with none last under Notes; within a group, as made.
 */
export function notebookGroups(notes: readonly NoteRecord[], search: string, held: ReadonlyMap<string, string> = new Map()): { readonly group: string; readonly ids: readonly string[] }[] {
  const needle = search.trim().toLocaleLowerCase();
  const groups = new Map<string, string[]>();
  for (const note of notes) {
    if (note.chapterId !== null || note.deletedAt !== null || !ownThread(note, notes)) continue;
    // Deep-fix r4: a card being edited stays where it was when Edit opened, whatever is typed, until Done.
    const kept = held.get(note.id);
    if (kept === undefined && needle !== '' && ![note.title, note.group, note.text].some((field) => field.toLocaleLowerCase().includes(needle))) continue;
    const group = kept ?? note.group.trim();
    groups.set(group, [...(groups.get(group) ?? []), note.id]);
  }
  const named = [...groups].filter(([group]) => group !== '');
  const none = groups.get('');
  return [...named.map(([group, ids]) => ({ group, ids })), ...(none === undefined ? [] : [{ group: '', ids: none }])];
}

/** D′3: a Notebook note shown as a thread (its replies under it): a reader's or invitee's; a reply on its own (table OR). */
const threadCarded = (note: NoteRecord | undefined): boolean => note !== undefined && (note.origin === 'reader' || note.origin === 'invitee' || note.parentId !== null);

/** D′3: the Notebook: the search, New note, the groups and their notes. */
function NotebookTab(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  const [held, setHeld] = createSignal<ReadonlyMap<string, string>>(new Map());
  const holding = (id: string) => (group: string | null): void => {
    const next = new Map(held());
    if (group === null) next.delete(id);
    else next.set(id, group);
    setHeld(next);
  };
  const groups = createMemo(() => notebookGroups(listed(shell), ui.noteSearch(), held()));
  const names = createMemo(() => groups().map((group) => group.group), [], { equals: sameIds });
  const groupNames = createMemo(() => [...new Set(listed(shell).flatMap((note) => (note.chapterId === null && note.deletedAt === null && note.group.trim() !== '' ? [note.group.trim()] : [])))]);
  return [
    el(
      'div',
      { class: 'sh-notebook-tools' },
      component(SearchField, {
        class: 'notes-search',
        inputClass: 'note-search',
        'aria-label': t('notes.search'),
        placeholder: t('notes.search'),
        get value() {
          return ui.noteSearch();
        },
        onChange: (value: string) => {
          ui.setNoteSearch(value);
        },
      }),
      component(Button, {
        variant: 'primary',
        size: 'sm',
        class: 'new-note',
        get disabled() {
          return !workspace.writable();
        },
        onClick: () =>
          void workspace.createNote({ chapterId: null, anchor: null, title: '', group: '', text: '', parentId: null }).then((id) => {
            if (id === null) return;
            ui.setNoteSearch('');
            queueMicrotask(() => {
              document.querySelector<HTMLButtonElement>(`[data-note="${id}"] .edit-note`)?.click();
            });
          }),
        children: t('notes.newNote'),
      }),
    ),
    el('datalist', { id: 'note-groups' }, each(groupNames, (group) => el('option', { value: group }))),
    when(
      () => groups().length === 0,
      () => el('p', { class: 'sh-empty-line' }, () => t(ui.noteSearch().trim() === '' ? 'notes.noneNotebook' : 'notes.noneFound')),
    ),
    each(names, (group) =>
      whenSome(
        () => groups().find((each) => each.group === group),
        (found) => {
          const heading = `notes-group-${encodeURIComponent(group || '-')}`;
          const ids = createMemo(() => found().ids, [], { equals: sameIds });
          return el('section', { class: 'sh-nb-group', 'aria-labelledby': heading }, el('h3', { id: heading }, group || t('notes.ungrouped')), el('ul', { class: 'sh-note-list' }, each(ids, (id) => (threadCarded(noteOf(shell, id)) ? ThreadCard(shell, id, placedThread) : NotebookCard(shell, id, holding(id))))));
        },
      ),
    ),
  ];
}

/**
 * CP5: what stays in a composer once `saved` is stored, the composer holding `now`: nothing when it is the same; the words
 * typed after it while it was being stored; or everything, when it was changed otherwise (no typed word is ever lost).
 */
export function leftAfterSaving(now: string, saved: string): string {
  return now.startsWith(saved) ? now.slice(saved.length).trimStart() : now;
}

/** CP1–CP4, NO1–NO4: the composer under the tab: the passage chosen (and dropping it), the note's words, Save note. */
function Composer(shell: Shell): Child {
  const { t, workspace, ui, sharing } = shell;
  // Part 38e, DN1: who sees the note is chosen as it is written; Only me unless the writer says otherwise (decision 76).
  const [audience, setAudience] = createSignal<'only-me' | 'shared'>('only-me');
  const [group, setGroup] = createSignal<KeyGroup>('everyone');
  const share = (): string | undefined => sharing.openShareOf(workspace.openBookId())?.id;
  const across = (): { readonly share: string; readonly group: KeyGroup } | null => {
    const id = share();
    return id === undefined || audience() === 'only-me' ? null : { share: id, group: group() };
  };
  // Table CK: the Notebook's composer is its book's.
  const key = (): string => (ui.notesTab() === 'notebook' ? workspace.notebookComposer() : `chapter:${workspace.chapterId() ?? ''}`);
  const passage = (): string | null => {
    const chosen = ui.passage();
    return ui.notesTab() === 'chapter' && chosen !== null && chosen.chapterId === workspace.chapterId() ? chosen.anchor.quote : null;
  };
  let field: HTMLTextAreaElement | undefined;
  // CP1: the field shows the composer of the tab and chapter shown, and follows its words (deep-fix r5: words brought back
  // from another page, or left after a save); what was typed is already there, so the caret stays.
  createEffect(
    on(
      () => workspace.composerText(key()),
      (text) => {
        if (field !== undefined && field.value !== text) field.value = text;
      },
    ),
  );
  const save = async (): Promise<void> => {
    const text = workspace.composerText(key());
    const chapterId = workspace.chapterId();
    const notebook = ui.notesTab() === 'notebook';
    if (text.trim() === '' || (!notebook && chapterId === null)) return;
    const chosen = ui.passage();
    const anchor = !notebook && chosen !== null && chosen.chapterId === chapterId ? chosen.anchor : null;
    const at = key();
    const id = await workspace.createNote({ chapterId: notebook ? null : chapterId, anchor, title: '', group: '', text, parentId: null, shared: across() });
    // CP3: kept when it wasn't stored.
    if (id === null) return;
    const left = leftAfterSaving(workspace.composerText(at), text);
    workspace.typeComposer(at, left);
    if (anchor !== null) ui.setPassage(null);
    queueMicrotask(() => {
      document.querySelector<HTMLElement>(`[data-note="${id}"]`)?.scrollIntoView({ block: 'nearest' });
    });
  };
  return el(
    'form',
    {
      class: 'sh-composer',
      onSubmit: (event: SubmitEvent) => {
        event.preventDefault();
        void save();
      },
    },
    whenSome(passage, (quote) =>
      el(
        'div',
        { class: 'sh-composer__passage' },
        el('span', {}, () => t('notes.onPassage', { quote: quote() })),
        component(Button, {
          variant: 'link',
          class: 'drop-passage',
          get 'aria-label'() {
            return t('notes.dropPassage', { quote: quote() });
          },
          onClick: () => {
            ui.setPassage(null);
            focusOn(shell, '#note-composer');
          },
          children: t('notes.dropPassageShort'),
        }),
      ),
    ),
    el('label', { for: 'note-composer', class: 'visually-hidden' }, t('notes.composerLabel')),
    component(Textarea, {
      id: 'note-composer',
      rows: 2,
      class: 'note-composer',
      get lang() {
        return workspace.book()?.metadata?.language ?? 'en-US';
      },
      get placeholder() {
        return ui.notesTab() === 'notebook' ? t('notes.composerNotebook') : t('notes.composerChapter');
      },
      // D′5: with no chapter open, This chapter's composer has nowhere to keep words.
      get readOnly() {
        return !workspace.writable() || (ui.notesTab() === 'chapter' && workspace.chapterId() === null);
      },
      defaultValue: untrack(() => workspace.composerText(key())),
      ref: (element: HTMLTextAreaElement) => {
        field = element;
      },
      onChange: (value: string) => {
        workspace.typeComposer(key(), value);
      },
      onKeyDown: (event: KeyboardEvent) => {
        // Ctrl+Enter (Cmd on a Mac) saves, as most apps' comment boxes do.
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void save();
        }
      },
    }),
    el(
      'div',
      { class: 'sh-composer__row' },
      // Part 38e: with no open share, a note is Only me, said as before; with one, the writer chooses (the design's Segmented).
      when(
        () => share() === undefined,
        () => el('span', { class: 'sh-vis' }, glyph('lock'), t('notes.onlyYou')),
        () =>
          component(SegmentedControl, {
            label: t('notes.whoSees'),
            class: 'note-audience',
            options: [
              { value: 'shared', label: t('notes.shared') },
              { value: 'only-me', label: t('notes.onlyMe') },
            ],
            get value() {
              return audience();
            },
            onChange: (value: string) => {
              setAudience(value === 'shared' ? 'shared' : 'only-me');
            },
            get fullWidth() {
              return shell.mode() === 'narrow';
            },
          }),
      ),
      // Decision 76 (not in the design): Shared names who, everyone on the book or one role group.
      when(
        () => across() !== null,
        () =>
          component(Select, {
            label: t('notes.sharedWith'),
            fieldClass: 'sh-composer__who',
            class: 'note-group',
            size: 'sm',
            fullWidth: true,
            'aria-describedby': 'note-composer-help',
            options: KEY_GROUPS.map((each) => ({ value: each, label: t(`notes.group.${each}`) })),
            get value() {
              return group();
            },
            onChange: (value: string) => {
              for (const each of KEY_GROUPS) if (each === value) setGroup(each);
            },
          }),
      ),
      // Design round d1: who comes before Save, in the page's order as on the screen (2.4.3).
      component(Button, {
        variant: 'primary',
        size: 'sm',
        type: 'submit',
        class: 'save-note',
        get disabled() {
          return !workspace.writable() || (ui.notesTab() === 'chapter' && workspace.chapterId() === null);
        },
        get fullWidth() {
          return shell.mode() === 'narrow';
        },
        children: t('notes.save'),
      }),
    ),
    el('p', { class: 'sh-composer__help', id: 'note-composer-help' }, () => (across() === null ? t('notes.composerHelp') : t('notes.sharedHelp', { group: group() === 'everyone' ? t('notes.everyone') : t(`notes.group.${group()}`) }))),
  );
}

/** D′1, Y′: the tabs (the library's Tabs: the arrow keys, Home and End move along them), each counting its notes. */
function NotesTabs(shell: Shell): Child {
  const { t, ui } = shell;
  const tabs: readonly TabItem[] = [
    {
      value: 'chapter',
      label: t('notes.thisChapter'),
      get count() {
        return openCount(shell);
      },
    },
    {
      value: 'notebook',
      label: t('notes.notebook'),
      get count() {
        return notebookCount(shell);
      },
    },
  ];
  return component(Tabs, {
    id: 'notes',
    class: 'notes-tabs',
    label: t('notes.title'),
    panelId: 'notes-tabpanel',
    tabs,
    get value() {
      return ui.notesTab();
    },
    onChange: (value: string) => {
      ui.setNotesTab(value === 'notebook' ? 'notebook' : 'chapter');
    },
  });
}

/**
 * Table D′: the panel: on a wide screen a complementary region beside the text, on a narrow one a sheet over a scrim
 * (D′10, a modal dialog: Close, Escape and the scrim close it); the tabs, the shown tab's notes, the refused ones counted
 * (D′7), and the composer.
 */
export function NotesPanel(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  const narrow = (): boolean => shell.mode() === 'narrow';
  const invalid = (): number => workspace.notes()?.invalid.length ?? 0;
  const openChapter = (): readonly string[] => {
    const chapterId = workspace.chapterId();
    return chapterId === null ? [] : [chapterId];
  };
  // CP4: a passage of another chapter is dropped as the chapter changes.
  createEffect(
    on(workspace.chapterId, (chapterId) => {
      const chosen = ui.passage();
      if (chosen !== null && chosen.chapterId !== chapterId) ui.setPassage(null);
    }),
  );
  const keys = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || event.defaultPrevented || !narrow()) return;
    event.preventDefault();
    closeNotes(shell);
  };
  return [
    when(narrow, () => el('div', { class: 'sh-sheet-scrim', 'aria-hidden': 'true', onClick: () => { closeNotes(shell); } })),
    el(
      // A section, which may be a dialog (narrow) as well as complementary (wide); an aside may not be a dialog.
      'section',
      {
        id: NOTES_PANEL,
        class: 'sh-notes-panel',
        'aria-label': () => (narrow() ? undefined : t('notes.title')),
        role: () => (narrow() ? 'dialog' : 'complementary'),
        'aria-modal': () => (narrow() ? 'true' : undefined),
        'aria-labelledby': () => (narrow() ? 'notes-sheet-title' : undefined),
        onKeyDown: keys,
      },
      when(narrow, () =>
        el(
          'div',
          { class: 'sh-sheet-handle' },
          el('span', { 'aria-hidden': 'true' }),
          el('h2', { class: 'visually-hidden', id: 'notes-sheet-title' }, t('notes.title')),
          // MK2 (deep-fix r7, design round 6): a phone's bar has no room for the check, so the sheet's top row holds it.
          NoteMarksToggle(shell),
          component(IconButton, {
            icon: 'close',
            label: t('notes.close'),
            title: false,
            class: 'close-notes',
            onClick: () => {
              closeNotes(shell);
            },
          }),
        ),
      ),
      NotesTabs(shell),
      el(
        'div',
        { class: 'sh-notes-scroll', role: 'tabpanel', id: 'notes-tabpanel', 'aria-labelledby': () => `notes-tab-${ui.notesTab()}` },
        when(
          () => ui.notesTab() === 'chapter',
          () => [
            // Made again for each chapter opened, so its fields and cards are the open chapter's.
            each(openChapter, (chapterId) => ChapterTab(shell, chapterId)),
            when(
              () => workspace.chapterId() === null,
              () => el('p', { class: 'sh-empty-line no-chapter-notes' }, t('notes.noChapter')),
            ),
          ],
          () => NotebookTab(shell),
        ),
        when(
          () => invalid() > 0,
          () => el('p', { class: 'unreadable-notes sh-help' }, () => t('notes.unreadable', { count: invalid() })),
        ),
      ),
      Composer(shell),
    ),
  ];
}
